import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@evorait/pace-environment';
import { ONLINE_HEADER } from '@evorait/pace-offline';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import {
    USER_STORE_BULK_MAX_KEYS,
    UserStoreBulkWriteEntry,
    UserStoreBulkWriteRequest,
    UserStoreDeleteResponse,
    UserStoreEntriesResponse,
    UserStoreEntry,
    UserStoreEntryResponse,
    UserStoreKeysResponse,
    UserStorePatchRequest,
    UserStoreWriteRequest,
} from '../../state/user-store/user-store.wire';
import { expectedHttpErrorStatuses } from '../online-only/expected-http-errors';

/**
 * The only place in the codebase that knows the `user-store` backend's HTTP shape and URLs (see
 * `ENDPOINTS.AzUserStore`). `UserStoreEffects` calls this instead of `HttpClient` directly, so the
 * wire contract — and the `{ data }` envelope every route wraps its payload in — stays defined
 * exactly once, in `user-store.wire.ts`.
 */
@Injectable({ providedIn: 'root' })
export class UserStoreApiService {
    private readonly http = inject(HttpClient);
    private readonly environmentService = inject(EnvironmentService);
    private readonly url = this.environmentService.apiBase + ENDPOINTS.AzUserStore;

    /**
     * Bulk read of just `keys`: the full entries of those that exist — a key without an entry is
     * simply missing from the result. The user store is shared by several domains (the user-store
     * slice, the device handover), so every reader names the keys it owns instead of reading
     * everything. A list longer than `USER_STORE_BULK_MAX_KEYS` is split into several requests;
     * an empty list sends none.
     */
    public getEntries(keys: readonly string[]): Observable<UserStoreEntry[]> {
        const requests = UserStoreApiService.inBulkSizedChunks(keys).map((chunk) =>
            this.http
                .get<UserStoreEntriesResponse>(this.url, { params: { keys: chunk.join(',') } })
                .pipe(map((response) => response.data)),
        );
        return requests.length === 0 ? of([]) : forkJoin(requests).pipe(map((pages) => pages.flat()));
    }

    /** The current user's keys, without their values. */
    public getKeys(): Observable<string[]> {
        return this.http.get<UserStoreKeysResponse>(this.url).pipe(map((response) => response.data));
    }

    /**
     * A single entry by key, or `undefined` if it does not exist — the backend 404s for a missing
     * key. That 404 is declared expected so `HttpOnlineInterceptor` does not show the user an
     * error window for a key that simply is not there (see `expectedHttpErrorStatuses`).
     */
    public getEntry(key: string): Observable<UserStoreEntry | undefined> {
        return this.http
            .get<UserStoreEntryResponse>(this.entryUrl(key), {
                context: expectedHttpErrorStatuses(HttpStatusCode.NotFound),
            })
            .pipe(
                map((response) => response.data),
                catchError((error: unknown) => {
                    if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound) {
                        return of(undefined);
                    }
                    return throwError(() => error);
                }),
            );
    }

    /**
     * Upserts the full value under `key` — create, or full replace of whatever was there. Returns
     * `void`, not the written entry: `ENDPOINTS.AzUserStore` is registered with
     * `PaceOfflineModule.forRoot`, so `OfflineInterceptorService` queues this PUT — even while
     * online, to survive a connectivity loss mid-request (see its "Even if online, cache the
     * request..." comment) — and answers with a synthetic `{ d: {} }` before the real backend has
     * necessarily seen it. Whatever entry this used to return was never a real one in that case,
     * so there is nothing genuine to hand back to the caller.
     *
     * `onlineOnly` opts out of that queue by sending `ONLINE_HEADER`, the same way `deleteEntry`
     * does: the PUT goes straight to the network and the returned observable reports the backend's
     * real outcome instead of "queued". Only a caller that both requires a genuine answer and knows
     * it is online should ask for it — the device-lock check-out does (see
     * `ConcurrentUsageService.uploadAll`). Everyday writes keep the queue, because they can happen
     * offline and would otherwise simply fail.
     */
    public putEntry(key: string, value: unknown, onlineOnly = false): Observable<void> {
        const request: UserStoreWriteRequest = { data: value };
        const options = onlineOnly ? { headers: new HttpHeaders({ [ONLINE_HEADER]: ONLINE_HEADER }) } : undefined;
        return this.http.put<unknown>(this.entryUrl(key), request, options).pipe(map(() => undefined));
    }

    /**
     * Bulk variant of `putEntry`: upserts every entry in one request (or one per
     * `USER_STORE_BULK_MAX_KEYS` entries), so the device check-out does not send one request per
     * key. Returns `void` and honours `onlineOnly` for the same reasons as `putEntry`. The backend
     * does not write the entries atomically: on an error some may have landed, and the caller
     * sends them all again — every entry is a full replace, so repeating is safe. An empty list
     * sends nothing.
     */
    public putEntries(entries: readonly UserStoreBulkWriteEntry[], onlineOnly = false): Observable<void> {
        const options = onlineOnly ? { headers: new HttpHeaders({ [ONLINE_HEADER]: ONLINE_HEADER }) } : undefined;
        const requests = UserStoreApiService.inBulkSizedChunks(entries).map((chunk) => {
            const request: UserStoreBulkWriteRequest = { entries: chunk };
            return this.http.put<unknown>(this.url, request, options);
        });
        return requests.length === 0 ? of(undefined) : forkJoin(requests).pipe(map(() => undefined));
    }

    /**
     * Shallow-merges `partial` into the existing entry's `data`, which must already be an object.
     *
     * NOT offline-queued: `OfflineInterceptorService.CACHEABLE_REQUEST_METHODS` only queues
     * `post`/`put`/`delete` requests, so a PATCH made while offline throws instead of being
     * queued. `UserStoreEffects` therefore never calls this — every write coming out of the slice
     * goes through `putEntry` with the complete mirrored value instead. Use this only for keys the
     * slice does not mirror (see `USER_STORE_UNMIRRORED_KEYS`), where the caller accepts that an
     * offline write fails loudly rather than being queued.
     */
    public patchEntry(key: string, partial: Record<string, unknown>): Observable<UserStoreEntry> {
        const request: UserStorePatchRequest = { data: partial };
        return this.http
            .patch<UserStoreEntryResponse>(this.entryUrl(key), request)
            .pipe(map((response) => response.data));
    }

    /**
     * Deletes the entry for `key`. Sends `ONLINE_HEADER` so `OfflineInterceptorService` passes it
     * straight to the network instead of queueing it like a normal cacheable write: the offline
     * request queue replays every request with `httpClient.post(...)` (see
     * `RequestQueueEffects.syncRequestQueue$`), so a queued DELETE would arrive at the backend as a
     * bodiless POST, which is rejected — it would sit in the shared queue failing forever.
     * `UserStoreEffects` compensates with its own `pendingDeletes` retry instead of the shared
     * queue (see `UserStoreState.pendingDeletes`).
     *
     * A 404 means `key` is already gone, which is exactly what a delete wants — treated as
     * success rather than an error, the same way `getEntry` treats a missing key as `undefined`,
     * and declared expected so `HttpOnlineInterceptor` raises no error window for it (see
     * `expectedHttpErrorStatuses`). Without that declaration every delete of an entry that never
     * reached Azure — written offline, deleted before the first upload — would end in an error
     * window, even though the caller treats it as done.
     */
    public deleteEntry(key: string): Observable<void> {
        return this.http
            .delete<UserStoreDeleteResponse>(this.entryUrl(key), {
                headers: new HttpHeaders({ [ONLINE_HEADER]: ONLINE_HEADER }),
                context: expectedHttpErrorStatuses(HttpStatusCode.NotFound),
            })
            .pipe(
                map(() => undefined),
                catchError((error: unknown) => {
                    if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound) {
                        return of(undefined);
                    }
                    return throwError(() => error);
                }),
            );
    }

    private entryUrl(key: string): string {
        return `${this.url}/${encodeURIComponent(key)}`;
    }

    /** Splits `items` into chunks the backend accepts in one bulk request. */
    private static inBulkSizedChunks<T>(items: readonly T[]): T[][] {
        const chunks: T[][] = [];
        for (let start = 0; start < items.length; start += USER_STORE_BULK_MAX_KEYS) {
            chunks.push(items.slice(start, start + USER_STORE_BULK_MAX_KEYS));
        }
        return chunks;
    }
}
